import { CommonModule } from '@angular/common';
import { Component, input, signal } from '@angular/core';
import { strToU8, zipSync } from 'fflate';
import { environment } from '../../../environments/environment';

export interface ChatRichTable {
  headers: string[];
  rows: string[][];
}

/** Estado de la vista de gráfica de una tabla. La elección de columnas es del usuario. */
type ChatChartType = 'barras' | 'lineas' | 'pastel';

interface ChatTableChartState {
  visible: boolean;
  labelColumn: number;
  valueColumn: number;
  type: ChatChartType;
}

export interface ChatLineChartPoint {
  x: number;
  y: number;
  label: string;
  valueText: string;
  mostrarEtiqueta: boolean;
}

export interface ChatLineChartData {
  path: string;
  points: ChatLineChartPoint[];
  height: number;
  baseY: number;
  maxText: string;
  minText: string;
  omitidas: number;
}

export interface ChatPieSlice {
  path: string;
  color: string;
  label: string;
  valueText: string;
  porcentaje: number;
}

export interface ChatPieChartData {
  slices: ChatPieSlice[];
  size: number;
  negativas: number;
  omitidas: number;
}

/** Una barra ya resuelta en coordenadas, lista para dibujar. */
export interface ChatChartBar {
  label: string;
  value: number;
  valueText: string;
  x: number;
  width: number;
  y: number;
}

export interface ChatChartData {
  bars: ChatChartBar[];
  height: number;
  zeroX: number;
  omitidas: number;
}

export interface ChatRichBlock {
  type: 'heading' | 'paragraph' | 'list';
  level?: number;
  heading?: string;
  paragraph?: string;
  items?: string[];
}

export interface ChatRichSource {
  title: string;
  location?: string;
  excerpt?: string;
  pageNumber?: number;
  imageUrl?: string;
  imageBase64?: string;
}

interface ChatRichSourceGroup {
  title: string;
  sources: ChatRichSource[];
  excerpt?: string;
}

interface StructuredListEntry {
  title: string;
  detail?: string;
}

interface ListItemParts {
  title?: string;
  detail?: string;
}

interface DocumentSummaryPair {
  title: string;
  summary?: string;
}

interface DocumentListEntry {
  title: string;
  summary?: string;
}

@Component({
  selector: 'app-chat-rich-response',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="space-y-4">
      <div class="rounded-xl border p-4" [ngClass]="responseCardClass()">
        <div class="space-y-3">
          @for (block of blocks(); track $index) {
            @if (block.type === 'heading') {
              <div
                class="font-semibold leading-snug text-gray-900 dark:text-gray-50"
                [ngClass]="headingClass(block.level)"
              >
                {{ block.heading }}
              </div>
            } @else if (block.type === 'paragraph') {
              <p class="whitespace-pre-wrap leading-relaxed text-gray-700 dark:text-gray-200">{{ block.paragraph }}</p>
            } @else {
              @if (hasDocumentEntries(block.items || [])) {
                <div class="space-y-3">
                  @for (entry of toDocumentEntries(block.items || []); track $index) {
                    <div>
                      @if (sourceForListItem(entry.title); as source) {
                        <a [href]="source.location" target="_blank" rel="noopener noreferrer" class="font-semibold text-emerald-700 underline decoration-emerald-400 underline-offset-2 hover:text-emerald-900 dark:text-emerald-300 dark:hover:text-emerald-100">
                          {{ entry.title }}
                        </a>
                      } @else {
                        <div class="font-semibold text-gray-800 dark:text-gray-100">{{ entry.title }}</div>
                      }
                      @if (entry.summary) {
                        <p class="mt-1 leading-relaxed text-gray-700 dark:text-gray-200">{{ entry.summary }}</p>
                      }
                    </div>
                  }
                </div>
              } @else {
                <ul class="list-disc pl-5 space-y-2" [ngClass]="bulletMarkerClass()">
                  @for (item of block.items || []; track $index) {
                    <li class="leading-relaxed text-gray-700 dark:text-gray-200">
                      @if (sourceForListItem(item); as source) {
                        <a [href]="source.location" target="_blank" rel="noopener noreferrer" class="text-emerald-700 underline decoration-emerald-400 underline-offset-2 hover:text-emerald-900 dark:text-emerald-300 dark:hover:text-emerald-100">
                          @if (splitListItem(item).title && splitListItem(item).detail) {
                            <span class="font-semibold text-gray-800 dark:text-gray-100">{{ splitListItem(item).title }}:</span>
                            <span> {{ splitListItem(item).detail }}</span>
                          } @else {
                            {{ item }}
                          }
                        </a>
                      } @else if (splitListItem(item).title && splitListItem(item).detail) {
                        <span class="font-semibold text-gray-800 dark:text-gray-100">{{ splitListItem(item).title }}:</span>
                        <span> {{ splitListItem(item).detail }}</span>
                      } @else {
                        {{ item }}
                      }
                    </li>
                  }
                </ul>
              }
            }
          }
          @if (!(blocks().length)) {
            <div class="whitespace-pre-wrap leading-relaxed">{{ summary() || fallbackContent() }}</div>
          }
        </div>
      </div>

      @if (tables().length) {
        @for (table of tables(); track $index; let tablaIdx = $index) {
          <div class="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-zinc-950 overflow-hidden">
            <div class="px-3 py-2 border-b border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-zinc-900 flex items-center justify-between gap-3">
              <div class="text-[11px] font-semibold uppercase tracking-wide" [ngClass]="accentHeadingClass()">Tabla de análisis</div>
              <div class="flex shrink-0 items-center gap-1.5">
              @if (chartsEnabled) {
              <button
                type="button"
                (click)="toggleChart(table, tablaIdx)"
                [title]="isChartVisible(tablaIdx) ? 'Volver a ver la tabla' : 'Ver estos datos como gráfica'"
                [attr.aria-pressed]="isChartVisible(tablaIdx)"
                class="shrink-0 inline-flex items-center gap-1 rounded px-1.5 py-1 border border-gray-200 text-gray-500 hover:bg-white hover:text-gray-700 dark:border-white/10 dark:text-gray-400 dark:hover:bg-white/5 transition-all duration-150 active:scale-95"
              >
                @if (isChartVisible(tablaIdx)) {
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" class="h-4 w-4" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 5.25h16.5M3.75 12h16.5M3.75 18.75h16.5" />
                  </svg>
                  <span class="text-[11px] font-medium">Tabla</span>
                } @else {
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" class="h-4 w-4" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 20.25h16.5M7 20.25V11m5 9.25V5.5m5 14.75v-6" />
                  </svg>
                  <span class="text-[11px] font-medium">Gráfica</span>
                }
              </button>
              }
              <button
                type="button"
                (click)="exportTableToExcel(table, tablaIdx)"
                title="Descargar esta tabla como archivo de Excel"
                aria-label="Descargar esta tabla como archivo de Excel"
                class="group shrink-0 inline-flex items-center gap-1 rounded px-1.5 py-1  border border-[rgb(20_182_131/0.3)] text-[rgb(20_182_131)] hover:bg-[rgb(20_182_131/0.1)] hover:border-[rgb(20_182_131)] dark:border-[rgb(20_182_131/0.4)] dark:text-[rgb(20_182_131)] dark:hover:bg-[rgb(20_182_131/0.15)] dark:hover:border-[rgb(20_182_131)] transition-all duration-150 active:scale-95 focus:outline-none focus:ring-2 focus:ring-[rgb(20_182_131/0.3)]"
              >
                <svg viewBox="0 0 24 24" class="h-5 w-5" aria-hidden="true">
                  <path fill="#107C41" d="M14 2H6.5A1.5 1.5 0 0 0 5 3.5v17A1.5 1.5 0 0 0 6.5 22h11a1.5 1.5 0 0 0 1.5-1.5V7l-5-5Z" />
                  <path fill="#0B5A2F" d="M14 2v3.5A1.5 1.5 0 0 0 15.5 7H19l-5-5Z" />
                  <path stroke="#fff" stroke-width="1.5" stroke-linecap="round" d="m9.3 12.8 5.4 6.4M14.7 12.8l-5.4 6.4" />
                </svg>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  class="h-3 w-3 text-gray-400 transition-colors group-hover:text-gray-600 dark:group-hover:text-gray-200"
                  aria-hidden="true"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v11m0 0 4-4m-4 4-4-4M4 19h16" />
                </svg>
              </button>
              </div>
            </div>

            @if (isChartVisible(tablaIdx)) {
              <div class="p-3">
                @if (numericColumns(table).length === 0) {
                  <div class="rounded-lg border border-dashed border-gray-300 p-6 text-center text-xs text-gray-500 dark:border-white/15 dark:text-gray-400">
                    Esta tabla no tiene ninguna columna con números, así que no hay nada que
                    graficar. Vuelve a la vista de tabla para consultarla.
                  </div>
                } @else {
                  <div class="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] text-gray-500 dark:text-gray-400">
                    <label class="flex items-center gap-1.5">
                      <span>Etiquetas:</span>
                      <select
                        class="rounded border border-gray-200 bg-white px-1.5 py-1 text-[11px] text-gray-700 dark:border-white/10 dark:bg-zinc-900 dark:text-gray-200"
                        [value]="chartState(tablaIdx).labelColumn"
                        (change)="setChartColumn(tablaIdx, 'labelColumn', $any($event.target).value)"
                      >
                        @for (header of table.headers; track $index) {
                          <option [value]="$index">{{ header }}</option>
                        }
                      </select>
                    </label>
                    <label class="flex items-center gap-1.5">
                      <span>Valores:</span>
                      <select
                        class="rounded border border-gray-200 bg-white px-1.5 py-1 text-[11px] text-gray-700 dark:border-white/10 dark:bg-zinc-900 dark:text-gray-200"
                        [value]="chartState(tablaIdx).valueColumn"
                        (change)="setChartColumn(tablaIdx, 'valueColumn', $any($event.target).value)"
                      >
                        @for (columna of numericColumns(table); track columna) {
                          <option [value]="columna">{{ table.headers[columna] }}</option>
                        }
                      </select>
                    </label>

                    <div class="ml-auto flex items-center gap-1" role="group" aria-label="Tipo de gráfica">
                      @for (tipo of chartTypes; track tipo.id) {
                        <button
                          type="button"
                          (click)="setChartType(tablaIdx, tipo.id)"
                          [title]="tipo.ayuda"
                          [attr.aria-label]="tipo.ayuda"
                          [attr.aria-pressed]="chartState(tablaIdx).type === tipo.id"
                          class="rounded p-1.5 transition-colors"
                          [ngClass]="chartState(tablaIdx).type === tipo.id
                            ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                            : 'border border-gray-200 text-gray-500 hover:bg-white dark:border-white/10 dark:text-gray-400 dark:hover:bg-white/5'"
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" class="h-4 w-4" aria-hidden="true">
                            @switch (tipo.id) {
                              @case ('barras') {
                                <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 20.25h16.5M7 20.25V11m5 9.25V5.5m5 14.75v-6" />
                              }
                              @case ('lineas') {
                                <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 20.25h16.5M6.5 16l4-4.5 3.5 3 4.5-6" />
                              }
                              @case ('pastel') {
                                <path stroke-linecap="round" stroke-linejoin="round" d="M12 3.75a8.25 8.25 0 1 0 8.25 8.25H12V3.75Z" />
                                <path stroke-linecap="round" stroke-linejoin="round" d="M14.75 3.9A8.25 8.25 0 0 1 20.1 9.25h-5.35V3.9Z" />
                              }
                            }
                          </svg>
                        </button>
                      }
                    </div>
                  </div>

                  @if (chartState(tablaIdx).type === 'lineas') {
                    @if (lineChartData(table, tablaIdx); as linea) {
                      <svg
                        [attr.viewBox]="'0 0 ' + chartWidth() + ' ' + linea.height"
                        class="w-full"
                        [attr.height]="linea.height"
                        role="img"
                        aria-label="Gráfica de líneas de los datos de la tabla"
                      >
                        <line x1="56" [attr.y1]="linea.baseY" [attr.x2]="chartWidth() - 16" [attr.y2]="linea.baseY" class="stroke-gray-200 dark:stroke-white/15" stroke-width="1" />
                        <text x="50" y="18" text-anchor="end" font-size="10" class="fill-gray-400">{{ linea.maxText }}</text>
                        <text x="50" [attr.y]="linea.baseY" text-anchor="end" font-size="10" class="fill-gray-400">{{ linea.minText }}</text>

                        <path [attr.d]="linea.path" fill="none" class="stroke-emerald-500" stroke-width="2" stroke-linejoin="round" stroke-linecap="round" />

                        @for (punto of linea.points; track $index) {
                          <circle [attr.cx]="punto.x" [attr.cy]="punto.y" r="3" class="fill-emerald-500" />
                          @if (punto.mostrarEtiqueta) {
                            <text
                              [attr.x]="punto.x"
                              [attr.y]="linea.baseY + 16"
                              text-anchor="middle"
                              font-size="9"
                              class="fill-gray-500 dark:fill-gray-400"
                            >{{ punto.label.length > 12 ? punto.label.slice(0, 11) + '…' : punto.label }}</text>
                          }
                        }
                      </svg>

                      @if (linea.omitidas > 0) {
                        <p class="mt-2 text-[11px] text-gray-400">
                          Se muestran los primeros 30 puntos; quedan {{ linea.omitidas }} fuera.
                        </p>
                      }
                    } @else {
                      <div class="rounded-lg border border-dashed border-gray-300 p-6 text-center text-xs text-gray-500 dark:border-white/15 dark:text-gray-400">
                        Hacen falta al menos dos filas con números para trazar una línea.
                      </div>
                    }
                  } @else if (chartState(tablaIdx).type === 'pastel') {
                    @if (pieChartData(table, tablaIdx); as pastel) {
                      <div class="flex flex-wrap items-center justify-center gap-6">
                        <svg
                          [attr.viewBox]="'0 0 ' + pastel.size + ' ' + pastel.size"
                          [attr.width]="pastel.size"
                          [attr.height]="pastel.size"
                          class="max-w-full"
                          role="img"
                          aria-label="Gráfica de pastel de los datos de la tabla"
                        >
                          @for (porcion of pastel.slices; track $index) {
                            <path [attr.d]="porcion.path" [attr.fill]="porcion.color" stroke="white" stroke-width="1.5" />
                          }
                        </svg>

                        <ul class="min-w-[160px] space-y-1.5 text-[11px]">
                          @for (porcion of pastel.slices; track $index) {
                            <li class="flex items-center gap-2">
                              <span class="h-2.5 w-2.5 shrink-0 rounded-sm" [style.background-color]="porcion.color"></span>
                              <span class="min-w-0 flex-1 truncate text-gray-600 dark:text-gray-300">{{ porcion.label }}</span>
                              <span class="shrink-0 tabular-nums font-medium text-gray-900 dark:text-gray-100">{{ porcion.porcentaje }}%</span>
                            </li>
                          }
                        </ul>
                      </div>

                      @if (pastel.negativas > 0 || pastel.omitidas > 0) {
                        <p class="mt-3 text-[11px] text-gray-400">
                          @if (pastel.negativas > 0) {
                            Se omitieron {{ pastel.negativas }} filas con valores negativos o en cero: un pastel reparte partes de un total.
                          }
                          @if (pastel.omitidas > 0) {
                            Se muestran las 12 primeras porciones; quedan {{ pastel.omitidas }} fuera.
                          }
                        </p>
                      }
                    } @else {
                      <div class="rounded-lg border border-dashed border-gray-300 p-6 text-center text-xs text-gray-500 dark:border-white/15 dark:text-gray-400">
                        No hay valores positivos que repartir, así que un pastel no diría nada.
                        Prueba con barras.
                      </div>
                    }
                  } @else if (chartData(table, tablaIdx); as grafica) {
                    <svg
                      [attr.viewBox]="'0 0 ' + chartWidth() + ' ' + grafica.height"
                      class="w-full"
                      [attr.height]="grafica.height"
                      role="img"
                      aria-label="Gráfica de barras de los datos de la tabla"
                    >
                      @for (bar of grafica.bars; track bar.label + $index) {
                        <text
                          x="144"
                          [attr.y]="bar.y + 13"
                          text-anchor="end"
                          class="fill-gray-500 dark:fill-gray-400"
                          font-size="11"
                        >{{ bar.label.length > 24 ? bar.label.slice(0, 23) + '…' : bar.label }}</text>
                        <rect
                          [attr.x]="bar.x"
                          [attr.y]="bar.y + 3"
                          [attr.width]="bar.width"
                          height="16"
                          rx="3"
                          class="fill-emerald-500/80"
                        />
                        <text
                          [attr.x]="bar.x + bar.width + 6"
                          [attr.y]="bar.y + 15"
                          class="fill-gray-600 dark:fill-gray-300"
                          font-size="10"
                        >{{ bar.valueText }}</text>
                      }
                      <line
                        [attr.x1]="grafica.zeroX"
                        [attr.x2]="grafica.zeroX"
                        y1="0"
                        [attr.y2]="grafica.height"
                        class="stroke-gray-200 dark:stroke-white/15"
                        stroke-width="1"
                      />
                    </svg>

                    @if (grafica.omitidas > 0) {
                      <p class="mt-2 text-[11px] text-gray-400">
                        Se muestran las primeras 30 filas; quedan {{ grafica.omitidas }} fuera de la gráfica.
                      </p>
                    }
                  } @else {
                    <div class="rounded-lg border border-dashed border-gray-300 p-6 text-center text-xs text-gray-500 dark:border-white/15 dark:text-gray-400">
                      No se pudieron leer números en la columna elegida. Prueba con otra.
                    </div>
                  }
                }
              </div>
            } @else {
            <div class="overflow-x-auto">
            <table class="min-w-full text-xs border-collapse">
              <thead>
                <tr class="bg-gray-50 dark:bg-zinc-900/80">
                  @for (header of table.headers; track $index) {
                    <th class="text-left px-3 py-2.5 border-b border-gray-200 dark:border-white/10 font-semibold text-gray-700 dark:text-gray-200 whitespace-nowrap">{{ header }}</th>
                  }
                </tr>
              </thead>
              <tbody>
                @for (row of table.rows; track $index) {
                  <tr class="odd:bg-white even:bg-gray-50/70 dark:odd:bg-zinc-950 dark:even:bg-zinc-900/35">
                    @for (cell of row; track $index) {
                      <td class="align-top px-3 py-2.5 border-b border-gray-100 dark:border-white/10 text-gray-700 dark:text-gray-200 leading-relaxed">
                        @if (sourceForListItem(cell); as source) {
                          <a [href]="source.location" target="_blank" rel="noopener noreferrer" class="text-emerald-700 underline decoration-emerald-400 underline-offset-2 hover:text-emerald-900 dark:text-emerald-300 dark:hover:text-emerald-100">
                            {{ cell }}
                          </a>
                        } @else {
                          {{ cell }}
                        }
                      </td>
                    }
                  </tr>
                }
              </tbody>
            </table>
            </div>
            }
          </div>
        }
      }

      @if (sources().length) {
        <div class="space-y-2">
          <button
            (click)="toggleSources()"
            class="text-[11px] px-2.5 py-1 rounded border transition-colors"
            [ngClass]="showMoreButtonClass()"
          >
            {{ showAllSources() ? 'Ocultar referencias' : ('Mostrar referencias (' + sources().length + ')') }}
          </button>
          @if (showAllSources()) {
            <div class="text-xs font-semibold uppercase tracking-wide" [ngClass]="accentHeadingClass()">Referencias</div>
            <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2">
              @for (sourceGroup of getVisibleSourceGroups(); track sourceGroup.title + '-' + $index) {
                <div class="rounded-lg border border-gray-200 dark:border-white/10 px-2.5 py-2 bg-gray-50/80 dark:bg-zinc-800/30">
                  <div class="text-xs font-semibold leading-snug text-gray-800 dark:text-gray-100 break-words">
                    @if (groupPrimaryLocation(sourceGroup.sources)) {
                      <a [href]="groupPrimaryLocation(sourceGroup.sources)" target="_blank" rel="noopener noreferrer" class="underline hover:text-gray-700 dark:hover:text-gray-300">
                        {{ sourceGroup.title }}
                      </a>
                    } @else {
                      {{ sourceGroup.title }}
                    }
                  </div>
                  @if (getPageLinkSources(sourceGroup.sources).length) {
                    <div class="mt-1 flex flex-wrap gap-x-2 gap-y-1 text-[11px]">
                      @for (source of getPageLinkSources(sourceGroup.sources); track source.location + '-' + source.pageNumber + '-' + $index) {
                        @if (source.location) {
                          <a [href]="source.location" target="_blank" rel="noopener noreferrer" class="underline hover:text-gray-700 dark:hover:text-gray-300">{{ sourcePageLabel(source) }}</a>
                        }
                      }
                    </div>
                  }
                  @if (getImageSources(sourceGroup.sources).length) {
                    <div class="mt-2 grid grid-cols-2 gap-2">
                      @for (source of getImageSources(sourceGroup.sources); track sourceImageHref(source) + '-' + $index) {
                        <a [href]="sourceImageHref(source)" target="_blank" rel="noopener noreferrer" class="block rounded-md overflow-hidden border border-gray-200 dark:border-white/10 hover:opacity-90 transition-opacity">
                          <img
                            [src]="sourceImageSrc(source)"
                            [alt]="source.title || sourceGroup.title || 'Referencia con imagen'"
                            class="h-24 w-full object-cover bg-white dark:bg-zinc-900"
                            loading="lazy"
                          />
                        </a>
                      }
                    </div>
                  }
                  @if (sourceGroup.excerpt) {
                    <div class="text-[11px] text-gray-500 dark:text-gray-400 mt-1 leading-snug">
                      {{ sourceGroup.excerpt }}
                    </div>
                  }
                </div>
              }
            </div>
          }
        </div>
      }
    </div>
  `
})
export class ChatRichResponseComponent {
  readonly summary = input<string | undefined>(undefined);
  readonly fallbackContent = input<string>('');
  readonly blocks = input<ChatRichBlock[]>([]);
  readonly tables = input<ChatRichTable[]>([]);
  readonly sources = input<ChatRichSource[]>([]);
  readonly accent = input<'amber' | 'orange' | 'emerald' | 'neutral'>('amber');

  readonly showAllSources = signal(false);

  /** Interruptor de la función, configurable en environment.ts sin tocar el backend. */
  readonly chartsEnabled = environment.features.charts;

  /** Tipos de gráfica que puede elegir el usuario, con la pista de cuándo sirve cada uno. */
  readonly chartTypes: { id: ChatChartType; etiqueta: string; ayuda: string }[] = [
    { id: 'barras', etiqueta: 'Barras', ayuda: 'Comparar cantidades entre categorías' },
    { id: 'lineas', etiqueta: 'Líneas', ayuda: 'Ver la evolución de una secuencia, por ejemplo por mes' },
    { id: 'pastel', etiqueta: 'Pastel', ayuda: 'Ver qué parte del total representa cada fila' }
  ];

  /** Filas máximas que se dibujan: más allá, las barras dejan de leerse. */
  private static readonly MaxChartRows = 30;

  private static readonly ChartWidth = 640;
  private static readonly ChartRowHeight = 26;
  private static readonly ChartLabelWidth = 150;

  /** Paleta del pastel: tonos distinguibles entre sí y también en escala de grises. */
  private static readonly PieColors = [
    '#10b981', '#0ea5e9', '#f59e0b', '#8b5cf6', '#ef4444', '#14b8a6',
    '#ec4899', '#84cc16', '#6366f1', '#f97316', '#06b6d4', '#a855f7'
  ];

  readonly chartStates = signal<Record<number, ChatTableChartState>>({});

  isChartVisible(tableIndex: number): boolean {
    return this.chartsEnabled && (this.chartStates()[tableIndex]?.visible ?? false);
  }

  chartState(tableIndex: number): ChatTableChartState {
    return this.chartStates()[tableIndex]
      ?? { visible: false, labelColumn: 0, valueColumn: -1, type: 'barras' };
  }

  setChartType(tableIndex: number, type: ChatChartType): void {
    const actual = this.chartState(tableIndex);
    this.chartStates.update(estados => ({
      ...estados,
      [tableIndex]: { ...actual, visible: true, type }
    }));
  }

  /**
   * Alterna entre la tabla y su gráfica. El botón se ofrece siempre: decidir si una tabla merece
   * gráfica es del usuario. Lo que nunca hacemos es dibujar una gráfica inventada: si no hay
   * ninguna columna con números, la vista lo dice en vez de enseñar barras sin sentido.
   */
  toggleChart(table: ChatRichTable, tableIndex: number): void {
    const actual = this.chartStates()[tableIndex];

    if (actual) {
      this.chartStates.update(estados => ({
        ...estados,
        [tableIndex]: { ...actual, visible: !actual.visible }
      }));
      return;
    }

    const numericas = this.numericColumns(table);
    const valueColumn = numericas.length > 0 ? numericas[0] : -1;
    const labelColumn = this.headers(table).findIndex((_, columna) => columna !== valueColumn);

    this.chartStates.update(estados => ({
      ...estados,
      [tableIndex]: {
        visible: true,
        labelColumn: labelColumn >= 0 ? labelColumn : 0,
        valueColumn,
        type: 'barras'
      }
    }));
  }

  setChartColumn(tableIndex: number, campo: 'labelColumn' | 'valueColumn', valor: string): void {
    const columna = Number(valor);
    if (!Number.isInteger(columna)) {
      return;
    }

    const actual = this.chartState(tableIndex);
    this.chartStates.update(estados => ({
      ...estados,
      [tableIndex]: { ...actual, visible: true, [campo]: columna }
    }));
  }

  headers(table: ChatRichTable): string[] {
    return table?.headers ?? [];
  }

  /** Columnas donde la mayoría de las celdas se interpretan como número. */
  numericColumns(table: ChatRichTable): number[] {
    const filas = table?.rows ?? [];
    if (filas.length === 0) {
      return [];
    }

    return this.headers(table)
      .map((_, columna) => columna)
      .filter(columna => {
        const validas = filas.filter(fila => this.parseNumericCell(fila?.[columna]) !== null).length;
        return validas >= Math.max(1, Math.ceil(filas.length * 0.6));
      });
  }

  /**
   * Interpreta una celda como número tolerando moneda, porcentajes, espacios y separadores de
   * miles. La ambigüedad entre "1.234" (mil doscientos) y "1.234" (uno coma dos) se resuelve por
   * la forma del propio número: un separador único seguido de tres dígitos es de miles.
   */
  parseNumericCell(bruto: string | undefined): number | null {
    const original = (bruto ?? '').trim();
    if (!original) {
      return null;
    }

    const negativo = original.startsWith('-') || /^\(.*\)$/.test(original);
    let texto = original.replace(/[^\d.,]/g, '');
    if (!texto || !/\d/.test(texto)) {
      return null;
    }

    const ultimaComa = texto.lastIndexOf(',');
    const ultimoPunto = texto.lastIndexOf('.');
    let separadorDecimal = '';

    if (ultimaComa >= 0 && ultimoPunto >= 0) {
      separadorDecimal = ultimaComa > ultimoPunto ? ',' : '.';
    } else if (ultimaComa >= 0 || ultimoPunto >= 0) {
      const separador = ultimaComa >= 0 ? ',' : '.';
      const apariciones = texto.split(separador).length - 1;
      const digitosDetras = texto.length - texto.lastIndexOf(separador) - 1;
      separadorDecimal = apariciones === 1 && digitosDetras !== 3 ? separador : '';
    }

    // Si no hay decimal, el separador que aparece es justamente el de miles: quitarlo a él y no
    // al contrario. Sin esto, "1.234" se leía como uno coma dos en vez de mil doscientos treinta y cuatro.
    const separadorMiles = separadorDecimal === ','
      ? '.'
      : separadorDecimal === '.'
        ? ','
        : ultimaComa >= 0
          ? ','
          : '.';
    texto = texto.split(separadorMiles).join('');
    if (separadorDecimal) {
      texto = texto.replace(separadorDecimal, '.');
    }

    const numero = Number(texto);
    if (!Number.isFinite(numero)) {
      return null;
    }

    return negativo ? -Math.abs(numero) : numero;
  }

  /** Convierte la tabla en barras horizontales: no necesitan rotar etiquetas para leerse. */
  chartData(table: ChatRichTable, tableIndex: number): ChatChartData | null {
    const estado = this.chartState(tableIndex);
    if (estado.valueColumn < 0) {
      return null;
    }

    const filas = table?.rows ?? [];
    const puntos: { label: string; value: number; valueText: string }[] = [];

    for (const fila of filas) {
      const valor = this.parseNumericCell(fila?.[estado.valueColumn]);
      if (valor === null) {
        continue;
      }

      puntos.push({
        label: (fila?.[estado.labelColumn] ?? '').trim() || '(sin etiqueta)',
        value: valor,
        valueText: (fila?.[estado.valueColumn] ?? '').trim()
      });
    }

    if (puntos.length === 0) {
      return null;
    }

    const visibles = puntos.slice(0, ChatRichResponseComponent.MaxChartRows);
    const minimo = Math.min(0, ...visibles.map(punto => punto.value));
    const maximo = Math.max(0, ...visibles.map(punto => punto.value));
    const rango = maximo - minimo || 1;

    const areaX = ChatRichResponseComponent.ChartLabelWidth;
    const areaAncho = ChatRichResponseComponent.ChartWidth - areaX - 70;
    const zeroX = areaX + ((0 - minimo) / rango) * areaAncho;

    const bars = visibles.map((punto, indice) => {
      const x = areaX + ((Math.min(punto.value, 0) - minimo) / rango) * areaAncho;
      const width = Math.max(1, (Math.abs(punto.value) / rango) * areaAncho);

      return {
        ...punto,
        x,
        width,
        y: indice * ChatRichResponseComponent.ChartRowHeight + 6
      };
    });

    return {
      bars,
      height: visibles.length * ChatRichResponseComponent.ChartRowHeight + 12,
      zeroX,
      omitidas: puntos.length - visibles.length
    };
  }

  chartWidth(): number {
    return ChatRichResponseComponent.ChartWidth;
  }

  /** Puntos ya interpretados de la columna elegida, comunes a los tres tipos de gráfica. */
  private chartPoints(table: ChatRichTable, tableIndex: number): { label: string; value: number; valueText: string }[] {
    const estado = this.chartState(tableIndex);
    if (estado.valueColumn < 0) {
      return [];
    }

    const puntos: { label: string; value: number; valueText: string }[] = [];
    for (const fila of table?.rows ?? []) {
      const valor = this.parseNumericCell(fila?.[estado.valueColumn]);
      if (valor === null) {
        continue;
      }

      puntos.push({
        label: (fila?.[estado.labelColumn] ?? '').trim() || '(sin etiqueta)',
        value: valor,
        valueText: (fila?.[estado.valueColumn] ?? '').trim()
      });
    }

    return puntos;
  }

  /** Línea: pensada para una secuencia, no para comparar categorías sueltas. */
  lineChartData(table: ChatRichTable, tableIndex: number): ChatLineChartData | null {
    const puntos = this.chartPoints(table, tableIndex);
    if (puntos.length < 2) {
      return null;
    }

    const visibles = puntos.slice(0, ChatRichResponseComponent.MaxChartRows);
    const valores = visibles.map(punto => punto.value);
    const maximo = Math.max(...valores);
    const minimo = Math.min(...valores);
    const rango = maximo - minimo || Math.abs(maximo) || 1;

    const ancho = ChatRichResponseComponent.ChartWidth;
    const alto = 240;
    const margenIzquierdo = 56;
    const margenDerecho = 16;
    const margenSuperior = 14;
    const areaAlto = alto - margenSuperior - 34;
    const areaAncho = ancho - margenIzquierdo - margenDerecho;

    // Con muchos puntos las etiquetas se pisan: se rotula uno de cada N.
    const paso = Math.ceil(visibles.length / 8);

    const points = visibles.map((punto, indice) => ({
      ...punto,
      x: margenIzquierdo + (visibles.length === 1 ? areaAncho / 2 : (indice / (visibles.length - 1)) * areaAncho),
      y: margenSuperior + ((maximo - punto.value) / rango) * areaAlto,
      mostrarEtiqueta: indice % paso === 0 || indice === visibles.length - 1
    }));

    return {
      path: points.map((punto, indice) => `${indice === 0 ? 'M' : 'L'}${punto.x.toFixed(1)} ${punto.y.toFixed(1)}`).join(' '),
      points,
      height: alto,
      baseY: margenSuperior + areaAlto,
      maxText: this.formatChartNumber(maximo),
      minText: this.formatChartNumber(minimo),
      omitidas: puntos.length - visibles.length
    };
  }

  /** Pastel: solo tiene sentido con partes de un total, así que descarta los negativos. */
  pieChartData(table: ChatRichTable, tableIndex: number): ChatPieChartData | null {
    const puntos = this.chartPoints(table, tableIndex);
    const positivos = puntos.filter(punto => punto.value > 0);
    if (positivos.length === 0) {
      return null;
    }

    const visibles = positivos.slice(0, 12);
    const total = visibles.reduce((suma, punto) => suma + punto.value, 0);
    if (total <= 0) {
      return null;
    }

    const size = 260;
    const centro = size / 2;
    const radio = centro - 8;
    let anguloInicial = -Math.PI / 2;

    const slices = visibles.map((punto, indice) => {
      const porcion = punto.value / total;
      const anguloFinal = anguloInicial + porcion * Math.PI * 2;
      const x1 = centro + radio * Math.cos(anguloInicial);
      const y1 = centro + radio * Math.sin(anguloInicial);
      const x2 = centro + radio * Math.cos(anguloFinal);
      const y2 = centro + radio * Math.sin(anguloFinal);
      const arcoGrande = porcion > 0.5 ? 1 : 0;

      // Una sola porción no se puede dibujar con un arco: se usa el círculo completo.
      const path = visibles.length === 1
        ? `M ${centro} ${centro - radio} A ${radio} ${radio} 0 1 1 ${centro - 0.01} ${centro - radio} Z`
        : `M ${centro} ${centro} L ${x1.toFixed(2)} ${y1.toFixed(2)} A ${radio} ${radio} 0 ${arcoGrande} 1 ${x2.toFixed(2)} ${y2.toFixed(2)} Z`;

      anguloInicial = anguloFinal;

      return {
        path,
        color: ChatRichResponseComponent.PieColors[indice % ChatRichResponseComponent.PieColors.length],
        label: punto.label,
        valueText: punto.valueText,
        porcentaje: Math.round(porcion * 1000) / 10
      };
    });

    return {
      slices,
      size,
      negativas: puntos.length - positivos.length,
      omitidas: positivos.length - visibles.length
    };
  }

  private formatChartNumber(valor: number): string {
    return new Intl.NumberFormat('es-MX', { maximumFractionDigits: 2 }).format(valor);
  }

  splitListItem(text: string): ListItemParts {
    const normalized = (text || '').trim();
    if (!normalized) {
      return {};
    }

    const separatorIndex = normalized.indexOf(':');
    if (separatorIndex <= 0 || separatorIndex >= normalized.length - 1) {
      return {};
    }

    const title = normalized.slice(0, separatorIndex).trim();
    const detail = normalized.slice(separatorIndex + 1).trim();
    const titleWords = title.split(/\s+/).filter(Boolean).length;

    if (!title || !detail || titleWords > 8 || /[.!?]$/.test(title)) {
      return {};
    }

    return { title, detail };
  }

  sourceForListItem(item: string): ChatRichSource | undefined {
    const parts = this.splitListItem(item);
    const itemTitle = this.normalizeComparableText(parts.title || item);
    if (!itemTitle) {
      return undefined;
    }

    return this.sources().find(source => {
      const title = this.normalizeComparableText(source.title);
      return this.isSafeExternalUrl(source.location || '')
        && title.length >= 4
        && (itemTitle === title || itemTitle.includes(title) || title.includes(itemTitle));
    });
  }

  hasDocumentEntries(items: string[]): boolean {
    return this.toDocumentEntries(items).length > 0;
  }

  toDocumentEntries(items: string[]): DocumentListEntry[] {
    const entries: DocumentListEntry[] = [];

    let index = 0;
    while (index < items.length) {
      const current = (items[index] || '').trim();
      const next = (items[index + 1] || '').trim();

      if (!current) {
        index += 1;
        continue;
      }

      const inlineDocument = this.tryParseInlineDocument(current);
      if (inlineDocument) {
        entries.push(inlineDocument);
        index += 1;
        continue;
      }

      const normalizedCurrent = this.unwrapMarkdownLinkLabel(current);
      if (this.looksLikeDocumentTitle(normalizedCurrent)) {
        const summary = next && !this.looksLikeDocumentTitle(this.unwrapMarkdownLinkLabel(next)) ? next : undefined;
        entries.push({ title: normalizedCurrent, summary });
        index += summary ? 2 : 1;
        continue;
      }

      if (entries.length > 0) {
        return [];
      }

      return [];
    }

    return entries;
  }

  private looksLikeDocumentTitle(text: string): boolean {
    return /\.(pdf|docx?|xlsx?|pptx?)$/i.test(text.trim());
  }

  private tryParseInlineDocument(text: string): DocumentListEntry | null {
    const normalized = this.unwrapMarkdownLinkLabel(text.trim());
    const inlineMatch = normalized.match(/^(.+?\.(pdf|docx?|xlsx?|pptx?))(?:\s*[:\-]\s*(.+))?$/i);
    if (!inlineMatch) {
      return null;
    }

    const title = inlineMatch[1]?.trim();
    const summary = inlineMatch[3]?.trim();
    if (!title) {
      return null;
    }

    return {
      title,
      summary: summary || undefined
    };
  }

  private unwrapMarkdownLinkLabel(text: string): string {
    const match = text.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (!match?.[1]) {
      return text;
    }

    return match[1].trim();
  }

  isSectionHeading(text: string): boolean {
    const normalized = text.trim();
    return normalized.endsWith(':')
      && normalized.length <= 72
      && !/[.!?]$/.test(normalized)
      && !normalized.includes('\n');
  }

  normalizeSectionHeading(text: string): string {
    return text.trim().replace(/:$/, '');
  }

  useCardGridForList(items: string[]): boolean {
    return items.length >= 5;
  }

  shouldRenderStructuredList(items: string[]): boolean {
    if (items.length < 2) {
      return false;
    }

    const entries = this.toStructuredEntries(items);
    const pairedEntries = entries.filter(entry => Boolean(entry.detail)).length;
    return pairedEntries >= Math.ceil(entries.length / 2);
  }

  toStructuredEntries(items: string[]): StructuredListEntry[] {
    const entries: StructuredListEntry[] = [];

    let index = 0;
    while (index < items.length) {
      const current = items[index].trim();
      const next = items[index + 1]?.trim();

      if (!current) {
        index += 1;
        continue;
      }

      if (this.isLikelyListTitle(current) && next && !this.isLikelyListTitle(next)) {
        entries.push({
          title: this.normalizeListTitle(current),
          detail: next
        });
        index += 2;
        continue;
      }

      entries.push({ title: this.normalizeListTitle(current) });
      index += 1;
    }

    return entries;
  }

  private isLikelyListTitle(text: string): boolean {
    const normalized = text.trim();
    if (!normalized) {
      return false;
    }

    if (/^[\p{L}\p{N}_\-\s./()]+\.[A-Za-z0-9]{2,5}$/u.test(normalized)) {
      return true;
    }

    const wordCount = normalized.split(/\s+/).length;
    const hasTerminalPunctuation = /[.!?]$/.test(normalized);
    return wordCount <= 10 && !hasTerminalPunctuation;
  }

  private normalizeListTitle(text: string): string {
    return text.replace(/:$/, '').trim();
  }

  isListSectionLabel(text: string): boolean {
    return /^[\p{L}\p{N}\s()\-/.%,]+:\s*$/u.test(text.trim());
  }

  getVisibleSources(): ChatRichSource[] {
    return this.showAllSources() ? this.sources() : [];
  }

  getVisibleSourceGroups(): ChatRichSourceGroup[] {
    if (!this.showAllSources()) {
      return [];
    }

    return this.groupSourcesByTitle(this.sources());
  }

  sourcePageLabel(source: ChatRichSource): string {
    return source.pageNumber && source.pageNumber > 0
      ? `Pagina ${source.pageNumber}`
      : 'Documento';
  }

  exportTableToExcel(table: ChatRichTable, tableIndex: number): void {
    const fileName = this.sanitizeFileName(`m31-tabla-${this.formatFileTimestamp(new Date())}-${tableIndex + 1}.xlsx`);
    const workbook = this.createXlsxWorkbook(table);
    const workbookBuffer = workbook.buffer.slice(
      workbook.byteOffset,
      workbook.byteOffset + workbook.byteLength
    ) as ArrayBuffer;
    const blob = new Blob([workbookBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  headingClass(level: number | undefined): string {
    if (level && level <= 2) {
      return 'text-base pt-1';
    }

    return 'text-sm pt-0.5';
  }

  hasSourceImage(source: ChatRichSource | undefined): boolean {
    if (!source) {
      return false;
    }

    return Boolean((source.imageUrl || '').trim() || (source.imageBase64 || '').trim());
  }

  allSourcesHaveImages(sources: ChatRichSource[]): boolean {
    return sources.length > 0 && sources.every(source => this.hasSourceImage(source));
  }

  getImageSources(sources: ChatRichSource[]): ChatRichSource[] {
    return sources.filter(source => this.hasSourceImage(source));
  }

  getPageLinkSources(sources: ChatRichSource[]): ChatRichSource[] {
    const grouped = new Map<string, { doc?: ChatRichSource; pages: Map<number, ChatRichSource> }>();

    for (const source of sources) {
      if (this.hasSourceImage(source)) {
        continue;
      }

      const location = (source.location || '').trim();
      if (!location) {
        continue;
      }

      const docKey = this.normalizeLocationForGrouping(location);
      let entry = grouped.get(docKey);
      if (!entry) {
        entry = { pages: new Map<number, ChatRichSource>() };
        grouped.set(docKey, entry);
      }

      const page = source.pageNumber && source.pageNumber > 0 ? source.pageNumber : 0;
      if (page > 0) {
        if (!entry.pages.has(page)) {
          entry.pages.set(page, source);
        }
      } else if (!entry.doc) {
        entry.doc = source;
      }
    }

    const result: ChatRichSource[] = [];
    for (const entry of grouped.values()) {
      if (entry.pages.size > 0) {
        const sortedPages = Array.from(entry.pages.entries())
          .sort((a, b) => a[0] - b[0])
          .map(([, source]) => source);
        result.push(...sortedPages);
      } else if (entry.doc) {
        result.push(entry.doc);
      }
    }

    return result;
  }

  groupPrimaryLocation(sources: ChatRichSource[]): string | undefined {
    const primaryPageOrDoc = this.getPageLinkSources(sources)[0];
    const primaryLocation = (primaryPageOrDoc?.location || '').trim();
    if (primaryLocation) {
      return primaryLocation;
    }

    for (const source of sources) {
      if (this.hasSourceImage(source)) {
        continue;
      }

      const location = (source.location || '').trim();
      if (location) {
        return location;
      }
    }

    return undefined;
  }

  private normalizeLocationForGrouping(location: string): string {
    try {
      const parsed = new URL(location);
      return `${parsed.origin}${parsed.pathname}`.toLowerCase();
    } catch {
      return location
        .split('#')[0]
        .split('?')[0]
        .trim()
        .toLowerCase();
    }
  }

  private normalizeComparableText(value: string): string {
    return (value || '')
      .replace(/\*\*/g, '')
      .replace(/`/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLocaleLowerCase();
  }

  private isSafeExternalUrl(value: string): boolean {
    try {
      const parsed = new URL(value);
      return parsed.protocol === 'https:' || parsed.protocol === 'http:';
    } catch {
      return false;
    }
  }

  sourceImageSrc(source: ChatRichSource | undefined): string {
    if (!source) {
      return '';
    }

    const imageUrl = (source.imageUrl || '').trim();
    if (imageUrl) {
      return imageUrl;
    }

    const imageBase64 = (source.imageBase64 || '').trim();
    if (!imageBase64) {
      return '';
    }

    if (imageBase64.startsWith('data:')) {
      return imageBase64;
    }

    return `data:image/png;base64,${imageBase64}`;
  }

  sourceImageHref(source: ChatRichSource | undefined): string {
    if (!source) {
      return '';
    }

    const imageUrl = (source.imageUrl || '').trim();
    if (imageUrl) {
      return imageUrl;
    }

    return this.sourceImageSrc(source);
  }

  private groupSourcesByTitle(sources: ChatRichSource[]): ChatRichSourceGroup[] {
    const grouped = new Map<string, ChatRichSourceGroup>();

    for (const source of sources) {
      const normalizedTitle = this.displayFileName(source);
      const key = normalizedTitle.toLowerCase();
      const existing = grouped.get(key);

      if (!existing) {
        grouped.set(key, {
          title: normalizedTitle || 'Referencia',
          sources: [source],
          excerpt: source.excerpt
        });
        continue;
      }

      existing.sources.push(source);
      if (!existing.excerpt && source.excerpt) {
        existing.excerpt = source.excerpt;
      }
    }

    return Array.from(grouped.values());
  }

  private displayFileName(source: ChatRichSource): string {
    const fromLocation = this.extractFileNameFromLocation(source.location);
    if (fromLocation) {
      return fromLocation;
    }

    const fromTitle = (source.title || '').trim();
    if (!fromTitle) {
      return 'Referencia';
    }

    return this.extractFileNameFromRaw(fromTitle) || fromTitle;
  }

  private extractFileNameFromLocation(location?: string): string | null {
    const raw = (location || '').trim();
    if (!raw) {
      return null;
    }

    try {
      const parsed = new URL(raw);
      const pathname = decodeURIComponent(parsed.pathname || '');
      return this.extractFileNameFromRaw(pathname);
    } catch {
      return this.extractFileNameFromRaw(raw);
    }
  }

  private extractFileNameFromRaw(value: string): string | null {
    const cleaned = value
      .split('#')[0]
      .split('?')[0]
      .trim();

    if (!cleaned) {
      return null;
    }

    const parts = cleaned.split(/[\\/]/).filter(Boolean);
    return parts.length > 0 ? parts[parts.length - 1] : cleaned;
  }

  private createXlsxWorkbook(table: ChatRichTable): Uint8Array {
    const headers = table.headers.length > 0 ? table.headers : ['Columna'];
    const rows = table.rows.length > 0 ? table.rows : [];
    const columnCount = Math.max(headers.length, ...rows.map(row => row.length), 1);
    const normalizedRows = [
      this.normalizeExcelRow(headers, columnCount),
      ...rows.map(row => this.normalizeExcelRow(row, columnCount))
    ];
    const files: Record<string, Uint8Array> = {
      '[Content_Types].xml': strToU8(this.xlsxContentTypesXml()),
      '_rels/.rels': strToU8(this.xlsxRootRelsXml()),
      'xl/workbook.xml': strToU8(this.xlsxWorkbookXml()),
      'xl/_rels/workbook.xml.rels': strToU8(this.xlsxWorkbookRelsXml()),
      'xl/styles.xml': strToU8(this.xlsxStylesXml()),
      'xl/worksheets/sheet1.xml': strToU8(this.xlsxWorksheetXml(normalizedRows))
    };

    return zipSync(files);
  }

  private normalizeExcelRow(row: string[], columnCount: number): string[] {
    return Array.from({ length: columnCount }, (_, index) => row[index] ?? '');
  }

  private xlsxWorksheetXml(rows: string[][]): string {
    const columnCount = Math.max(...rows.map(row => row.length), 1);
    const cols = Array.from({ length: columnCount }, (_, index) => {
      const maxLength = Math.max(...rows.map(row => (row[index] ?? '').length), 8);
      const width = Math.min(Math.max(maxLength + 2, 10), 48);
      return `<col min="${index + 1}" max="${index + 1}" width="${width}" customWidth="1"/>`;
    }).join('');
    const sheetRows = rows.map((row, rowIndex) => {
      const rowNumber = rowIndex + 1;
      const cells = row.map((cell, columnIndex) => {
        const reference = `${this.excelColumnName(columnIndex + 1)}${rowNumber}`;
        const style = rowIndex === 0 ? ' s="1"' : '';
        return `<c r="${reference}" t="inlineStr"${style}><is><t>${this.escapeXml(cell)}</t></is></c>`;
      }).join('');

      return `<row r="${rowNumber}">${cells}</row>`;
    }).join('');

    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <cols>${cols}</cols>
  <sheetData>${sheetRows}</sheetData>
</worksheet>`;
  }

  private xlsxContentTypesXml(): string {
    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
</Types>`;
  }

  private xlsxRootRelsXml(): string {
    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;
  }

  private xlsxWorkbookXml(): string {
    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets>
    <sheet name="Tabla" sheetId="1" r:id="rId1"/>
  </sheets>
</workbook>`;
  }

  private xlsxWorkbookRelsXml(): string {
    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`;
  }

  private xlsxStylesXml(): string {
    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <fonts count="2">
    <font><sz val="11"/><color theme="1"/><name val="Calibri"/><family val="2"/></font>
    <font><b/><sz val="11"/><color theme="1"/><name val="Calibri"/><family val="2"/></font>
  </fonts>
  <fills count="2">
    <fill><patternFill patternType="none"/></fill>
    <fill><patternFill patternType="gray125"/></fill>
  </fills>
  <borders count="1">
    <border><left/><right/><top/><bottom/><diagonal/></border>
  </borders>
  <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
  <cellXfs count="2">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
    <xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>
  </cellXfs>
</styleSheet>`;
  }

  private excelColumnName(index: number): string {
    let value = index;
    let name = '';

    while (value > 0) {
      const remainder = (value - 1) % 26;
      name = String.fromCharCode(65 + remainder) + name;
      value = Math.floor((value - 1) / 26);
    }

    return name;
  }

  private escapeXml(value: string): string {
    return String(value)
      .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  }

  private sanitizeFileName(value: string): string {
    return value.replace(/[^a-zA-Z0-9._-]/g, '-');
  }

  private formatFileTimestamp(date: Date): string {
    const pad = (part: number) => String(part).padStart(2, '0');
    return [
      date.getFullYear(),
      pad(date.getMonth() + 1),
      pad(date.getDate())
    ].join('')
      + '-'
      + [
        pad(date.getHours()),
        pad(date.getMinutes()),
        pad(date.getSeconds())
      ].join('');
  }

  toggleSources(): void {
    this.showAllSources.set(!this.showAllSources());
  }

  responseCardClass(): string {
    if (this.accent() === 'neutral') {
      return 'border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-zinc-900/40';
    }

    if (this.accent() === 'emerald') {
      return 'border-emerald-200/70 dark:border-emerald-700/40 bg-emerald-50/80 dark:bg-emerald-900/20';
    }

    return this.accent() === 'orange'
      ? 'border-orange-200/70 dark:border-orange-700/40 bg-orange-50/80 dark:bg-orange-900/20'
      : 'border-amber-200/70 dark:border-amber-700/40 bg-amber-50/80 dark:bg-amber-900/20';
  }

  accentHeadingClass(): string {
    if (this.accent() === 'neutral') {
      return 'text-gray-700 dark:text-gray-300';
    }

    if (this.accent() === 'emerald') {
      return 'text-emerald-700 dark:text-emerald-400';
    }

    return this.accent() === 'orange'
      ? 'text-orange-700 dark:text-orange-400'
      : 'text-amber-700 dark:text-amber-400';
  }

  sectionChipClass(): string {
    if (this.accent() === 'neutral') {
      return 'bg-gray-100 text-gray-700 dark:bg-zinc-800 dark:text-gray-300';
    }

    if (this.accent() === 'emerald') {
      return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300';
    }

    return this.accent() === 'orange'
      ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300'
      : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300';
  }

  bulletMarkerClass(): string {
    if (this.accent() === 'neutral') {
      return 'marker:text-gray-500';
    }

    if (this.accent() === 'emerald') {
      return 'marker:text-emerald-500';
    }

    return this.accent() === 'orange' ? 'marker:text-orange-500' : 'marker:text-amber-500';
  }

  listCardClass(): string {
    if (this.accent() === 'neutral') {
      return 'border-gray-200 bg-gray-50 dark:border-white/10 dark:bg-zinc-900/40';
    }

    if (this.accent() === 'emerald') {
      return 'border-emerald-200/70 bg-emerald-50/60 dark:border-emerald-700/40 dark:bg-emerald-900/10';
    }

    return this.accent() === 'orange'
      ? 'border-orange-200/70 bg-orange-50/60 dark:border-orange-700/40 dark:bg-orange-900/10'
      : 'border-amber-200/70 bg-amber-50/60 dark:border-amber-700/40 dark:bg-amber-900/10';
  }

  showMoreButtonClass(): string {
    if (this.accent() === 'neutral') {
      return 'border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-white/20 dark:text-gray-300 dark:hover:bg-zinc-800';
    }

    if (this.accent() === 'emerald') {
      return 'border-emerald-200 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-700/50 dark:text-emerald-300 dark:hover:bg-emerald-900/30';
    }

    return this.accent() === 'orange'
      ? 'border-orange-200 text-orange-700 hover:bg-orange-50 dark:border-orange-700/50 dark:text-orange-300 dark:hover:bg-orange-900/30'
      : 'border-amber-200 text-amber-700 hover:bg-amber-50 dark:border-amber-700/50 dark:text-amber-300 dark:hover:bg-amber-900/30';
  }
}
